import React, { useState, useCallback } from 'react';
import Modal from './Modal';
import Button from './Button';
import useCopy from '../util/useCopy';
import { updateUserPropertiesMutation } from '../resources/Auth/mutations';
import { CurrentUserMustResource } from '../resources/Auth/CurrentUserMust';
import SnailIcon from './SnailIcon';
import SnailStrikeThroughIcon from './SnailStrikeThroughIcon';
import Slider from './Slider';

const DEFAULT_DELAY_SECONDS = 15;

const SLIDER_MIN = 0;
const SLIDER_MAX = 120;
const SLIDER_STEP = 1;
const TICKS = [0, 30, 60, 90, 120];

const SpoilerDelayModal = ({
  isOpen,
  onClose,
  initialValue,
}: {
  isOpen: boolean;
  onClose: () => void;
  initialValue?: number;
}) => {
  const copy = useCopy();
  const currentUser = CurrentUserMustResource.use();
  const hasNeverSetDelay = currentUser.spoilerDelayMs === undefined || currentUser.spoilerDelayMs === null;
  const currentDelayMs = initialValue ?? (currentUser.spoilerDelayMs || 0);
  const currentDelaySeconds = Math.round(currentDelayMs / 1000);

  const [sliderValue, setSliderValue] = useState(() => currentDelaySeconds);
  const [lastNonZeroValue, setLastNonZeroValue] = useState(
    () => currentDelaySeconds || DEFAULT_DELAY_SECONDS,
  );

  const isLive = sliderValue === 0;
  const [isSaving, setIsSaving] = useState(false);

  const handleSliderChange = useCallback((value: number) => {
    setSliderValue(value);
    if (value > 0) setLastNonZeroValue(value);
  }, []);

  const handleToggleMute = useCallback(() => {
    if (isLive) {
      setSliderValue(lastNonZeroValue);
    } else {
      setSliderValue(0);
    }
  }, [isLive, lastNonZeroValue]);

  const handleSave = useCallback(async () => {
    const ms = sliderValue * 1000;
    setIsSaving(true);
    await updateUserPropertiesMutation({
      userId: currentUser.id,
      spoilerDelayMs: ms,
    });
    setIsSaving(false);
    onClose();
  }, [currentUser.id, sliderValue, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-1 flex-col items-start justify-end px-5">
        <SnailIcon className="mb-2 h-8 w-8 text-neutral-500" />
        <div className="text-xl font-bold">{copy.spoilerDelay}</div>
        <p className="mt-2 text-left text-sm text-neutral-600">
          {copy.spoilerDelayExplanation}
        </p>
      </div>
      <div className="flex flex-1 flex-col justify-start px-5 pt-2">
        <div className="-mx-4 rounded-xl border-4 border-neutral-100 px-4 py-3">
          <div className="flex items-baseline gap-2">
            <div
              className={
                isLive
                  ? 'text-md font-medium uppercase text-rose-default'
                  : 'text-md font-medium uppercase text-purple-900'
              }
            >
              {isLive ? copy.noDelay : copy.delay}
            </div>
            {!isLive && (
              <span className="text-sm font-medium text-rose-default">
                {sliderValue}s
              </span>
            )}
          </div>
          <div className="mt-3 flex items-start gap-3">
            <button
              type="button"
              onClick={handleToggleMute}
              disabled={isLive}
              className={
                isLive
                  ? 'flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-neutral-100 text-neutral-300 cursor-not-allowed'
                  : 'flex h-8 w-8 flex-none items-center justify-center rounded-lg border-2 border-neutral-300 bg-neutral-100 text-neutral-400 cursor-pointer'
              }
            >
              <SnailStrikeThroughIcon className="h-5 w-5" />
            </button>
            <div className="min-w-0 flex-1">
              <Slider
                min={SLIDER_MIN}
                max={SLIDER_MAX}
                step={SLIDER_STEP}
                value={sliderValue}
                onChange={handleSliderChange}
              />
              <div className="mt-1 flex justify-between text-xs text-neutral-400">
                {TICKS.map((tick) => (
                  <span key={tick}>{tick}s</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal.BottomActions>
        <Button onClick={handleSave} isLoading={isSaving}>
          {hasNeverSetDelay && isLive ? copy.gotIt : copy.save}
        </Button>
      </Modal.BottomActions>
    </Modal>
  );
};

export default SpoilerDelayModal;
